import type { ContactDetail } from '@secretario/shared';
import { PATIENT_STATUS_LABEL } from '@secretario/shared';
import { usePatientLinks } from '../lib/patientLinks.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useParams } from 'wouter';
import {
  deleteContactOverride,
  getContactDetail,
  saveContactOverride,
  updateContact,
} from '../lib/api/personal.js';
import { characterImage, DEFAULT_CHARACTER } from '../lib/characters.js';
import {
  catColor,
  catInfo,
  readCustomCategories,
  type CustomCategory,
} from '../lib/contactCategories.js';
import { useProfile, useUpdateProfile } from '../lib/useProfile.js';
import { ensureMatrixClient } from '../lib/matrix/client.js';
import { startDirectMessage } from '../lib/matrix/rooms.js';
import { inviteLink, inviteText } from '../lib/invite.js';
import { INVITES_KEY, recordInvite } from '../lib/api/invites.js';
import { shareText } from '../lib/native.js';
import { useSession } from '../lib/session.js';
import '../components/messaging/recovery-sheet.css';
import './ported/contato-detalhe.css';
import './ported/contato-detalhe-extra.css';
import { BackIcon } from '../components/chrome/BackIcon.js';
import { PersonAvatar, PersonGlyph } from '../components/chrome/PersonAvatar.js';
import { ProAccountTag, useBusinessCard } from '../components/plan/ProSeal.js';
import { usePersonPhoto, userIdOf } from '../lib/matrix/photos.js';
import { useMatrixClient } from '../lib/matrix/useMatrix.js';
import { IconChat, IconChatPlus, PageActions } from '../components/chrome/icons.js';
import { GlyphChat, GlyphChatOff, GlyphCheckCircle, GlyphEye } from '../components/ui/Glyphs.js';
import { Skeleton } from '../components/ui/Skeleton.js';

const CUSTOM_CAT_COLORS = [
  '#8D5EB7',
  '#E15F8E',
  '#3A5BC7',
  '#0E9F6E',
  '#E1A100',
  '#0891B2',
  '#C4564C',
];
function slugifyCategoryName(name: string) {
  const slug = name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return `custom_${slug || Date.now()}`;
}

// Categorias oferecidas ao dono (linguagem amigável, sem jargão).
const CATEGORY_CHOICES: { value: string; label: string }[] = [
  { value: 'familia', label: 'Família' },
  { value: 'amigos', label: 'Amigos' },
  { value: 'namorado', label: 'Amor' },
  { value: 'trabalho', label: 'Trabalho' },
  { value: 'clientes', label: 'Clientes' },
  { value: 'leads', label: 'Leads' },
  { value: 'parceiros', label: 'Parceiros' },
  { value: 'servicos', label: 'Serviços' },
  { value: 'comunidade', label: 'Comunidade' },
  { value: 'outro', label: 'Outros' },
];

type Mode = 'padrao' | 'auto' | 'aprovar' | 'pausado';

const MODES: { id: Mode; icon: ReactNode; title: string; desc: (n: string) => string }[] = [
  {
    id: 'padrao',
    icon: <GlyphCheckCircle />,
    title: 'Segue o padrão',
    desc: () => 'Usa o jeito geral do seu secretário',
  },
  {
    id: 'auto',
    icon: <GlyphChat />,
    title: 'Responde sozinho',
    desc: (n) => `Cuida de ${n} sem te incomodar`,
  },
  {
    id: 'aprovar',
    icon: <GlyphEye />,
    title: 'Me mostra antes',
    desc: (n) => `Você aprova cada resposta pra ${n}`,
  },
  {
    id: 'pausado',
    icon: <GlyphChatOff />,
    title: 'Não responde',
    desc: (n) => `Fica quietinho com ${n}`,
  },
];

function modeFromOverride(ov: ContactDetail['override']): Mode {
  if (!ov) return 'padrao';
  if (ov.neverAutomate) return 'pausado';
  if (ov.requiresApproval) return 'aprovar';
  return 'auto';
}

function firstName(name?: string | null) {
  const n = (name || '').trim();
  if (!n) return 'esse contato';
  return n.split(/\s+/)[0] ?? n;
}

function formatWhen(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function ContatoDetalhePage() {
  const { contactId = '' } = useParams<{ contactId: string }>();
  const [, navigate] = useLocation();
  const [openingChat, setOpeningChat] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteNote, setInviteNote] = useState('');
  const session = useSession();
  const qc = useQueryClient();

  // Quem ainda não está no Secretário recebe o convite pela folha de compartilhar do aparelho.
  async function sendInvite() {
    const username = session?.user.username;
    if (!username) return;
    const text = inviteText(username);
    const link = inviteLink(username);
    try {
      if (await shareText(text, link)) {
        await recordInvite(contactId);
        void qc.invalidateQueries({ queryKey: INVITES_KEY });
        setInviting(false);
        return;
      }
      await navigator.clipboard.writeText(`${text}\n${link}`);
      await recordInvite(contactId);
      void qc.invalidateQueries({ queryKey: INVITES_KEY });
      setInviteNote('Convite copiado. É só colar na conversa com a pessoa.');
    } catch {
      setInviteNote('Não consegui abrir o compartilhamento agora.');
    }
  }

  // Contato que já está no Secretário: abre (ou retoma) a conversa protegida.
  async function openChat(username: string) {
    if (openingChat) return;
    setOpeningChat(true);
    try {
      const client = await ensureMatrixClient();
      if (!client) throw new Error('mensageiro indisponível');
      const roomId = await startDirectMessage(client, `@${username}:${client.getDomain()}`);
      navigate(`/conversas/${encodeURIComponent(roomId)}`);
    } catch {
      setOpeningChat(false);
    }
  }

  const detailQ = useQuery({
    queryKey: ['contact-detail', contactId],
    queryFn: () => getContactDetail(contactId),
    enabled: !!contactId,
    staleTime: 10_000,
  });
  const profileQ = useProfile();
  const updateProfile = useUpdateProfile();

  const detail = detailQ.data ?? null;
  const contact = detail?.contact ?? null;
  const business = useBusinessCard(contact?.appUsername);
  const { client: photoClient } = useMatrixClient();
  const photoUrl = usePersonPhoto(
    photoClient,
    photoClient && contact?.appUsername ? userIdOf(photoClient, contact.appUsername) : null,
  );
  const agentName = profileQ.data?.secretaryName?.trim() || 'Seu secretário';
  const customCategories = useMemo(
    () => readCustomCategories(profileQ.data?.preferences),
    [profileQ.data],
  );
  const info = catInfo(contact?.category, customCategories);
  const patientLink = usePatientLinks().byContact.get(contactId) ?? null;
  const nome = firstName(contact?.displayName);

  // ── Estado local (hidrata do servidor) ──
  const [mode, setMode] = useState<Mode>('padrao');
  const [category, setCategory] = useState<string>('');
  const [name, setName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [notes, setNotes] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [busyMode, setBusyMode] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [savedInfo, setSavedInfo] = useState(false);

  // ── Nova categoria personalizada ("+") ──
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [savingNewCat, setSavingNewCat] = useState(false);

  useEffect(() => {
    if (hydrated || !detail || !contact) return;
    setMode(modeFromOverride(detail.override));
    setCategory(contact.category ?? '');
    setName(contact.displayName ?? '');
    setPreferredName(contact.preferredName ?? '');
    setNotes(contact.notes ?? '');
    setHydrated(true);
  }, [detail, contact, hydrated]);

  async function applyMode(next: Mode) {
    if (next === mode || !contactId) return;
    const prev = mode;
    setMode(next);
    setBusyMode(true);
    // O mesmo atendimento vale no WhatsApp (só Sec Full): o override é idêntico lá.
    const override =
      next === 'padrao'
        ? null
        : next === 'auto'
          ? { automationMode: 'automatico' as const, neverAutomate: false, requiresApproval: false }
          : next === 'aprovar'
            ? { automationMode: 'semiauto' as const, requiresApproval: true, neverAutomate: false }
            : {
                automationMode: 'desativado' as const,
                neverAutomate: true,
                requiresApproval: false,
              };
    try {
      if (override === null) await deleteContactOverride(contactId);
      else await saveContactOverride(contactId, override);
      // Espelha no WhatsApp conectado. A variável de ambiente crua tira este trecho
      // dos pacotes padrão; melhor esforço, nunca atrapalha o salvamento no app.
      if (import.meta.env.VITE_EDITION === 'full') {
        try {
          const { legacyMirrorOverride } = await import('../lib/full/legacy.js');
          await legacyMirrorOverride(contact?.phone, override);
        } catch {
          /* o WhatsApp pode estar fora do ar; o app já salvou */
        }
      }
      await qc.invalidateQueries({ queryKey: ['contact-detail', contactId] });
    } catch {
      setMode(prev); // reverte se falhar
    } finally {
      setBusyMode(false);
    }
  }

  async function pickCategory(value: string) {
    if (!contactId) return;
    const prev = category;
    setCategory(value);
    try {
      await updateContact(contactId, { category: value });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['contact-detail', contactId] }),
        qc.invalidateQueries({ queryKey: ['contacts-page'] }),
        qc.invalidateQueries({ queryKey: ['contacts-category-counts'] }),
      ]);
    } catch {
      setCategory(prev);
    }
  }

  async function createCustomCategory() {
    if (!newCatName.trim() || savingNewCat) return;
    setSavingNewCat(true);
    try {
      const id = slugifyCategoryName(newCatName);
      const color =
        CUSTOM_CAT_COLORS[customCategories.length % CUSTOM_CAT_COLORS.length] ?? '#8D5EB7';
      const created: CustomCategory = {
        id,
        label: newCatName.trim().slice(0, 30),
        c: color,
      };
      await updateProfile.mutateAsync({
        preferences: { customCategories: [...customCategories, created] },
      });
      setShowNewCat(false);
      setNewCatName('');
      await pickCategory(id);
    } finally {
      setSavingNewCat(false);
    }
  }

  async function saveInfo() {
    if (!contactId) return;
    setSavingInfo(true);
    setSavedInfo(false);
    try {
      await updateContact(contactId, {
        ...(name.trim() ? { displayName: name.trim() } : {}),
        preferredName: preferredName.trim() || null,
        notes: notes.trim() || null,
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['contact-detail', contactId] }),
        qc.invalidateQueries({ queryKey: ['contacts-page'] }),
      ]);
      setSavedInfo(true);
      setTimeout(() => setSavedInfo(false), 2200);
    } catch {
      /* silencioso */
    } finally {
      setSavingInfo(false);
    }
  }

  // ── Atividade recente (amigável) ──
  const timeline = useMemo(
    () =>
      (detail?.activity ?? [])
        .map((a) => ({
          id: a.id,
          kind: a.kind,
          text: a.text,
          when: formatWhen(a.createdAt),
          ts: Date.parse(a.createdAt) || 0,
        }))
        .sort((a, b) => b.ts - a.ts)
        .slice(0, 5),
    [detail],
  );

  const heroAccent = { ['--cc' as string]: info.c } as React.CSSProperties;

  return (
    <div className="cd">
      <div className="cd-wrap">
        <header className="cd-hd">
          <Link href="/contatos" className="cd-back back-btn" aria-label="Voltar para Contatos">
            <BackIcon />
          </Link>
          {contact && (
            <PageActions>
              <button
                className={`icon-btn${contact.appUsername ? ' primary' : ''}`}
                aria-label={
                  contact.appUsername
                    ? `Enviar mensagem para ${contact.displayName || 'este contato'}`
                    : `Convidar ${contact.displayName || 'este contato'} para o Secretário`
                }
                onClick={() =>
                  contact.appUsername ? void openChat(contact.appUsername) : setInviting(true)
                }
                disabled={openingChat}
              >
                {contact.appUsername ? <IconChat /> : <IconChatPlus />}
              </button>
            </PageActions>
          )}
        </header>

        {detailQ.isLoading || (!hydrated && !detailQ.isError) ? (
          <div className="cd-loading">
            <Skeleton variant="person" label="Abrindo o contato" />
          </div>
        ) : !contact ? (
          <div className="cd-empty">
            <i className="cd-empty-ic" aria-hidden="true">
              <PersonGlyph />
            </i>
            <b>Contato não encontrado</b>
            <Link href="/contatos" className="cd-emptylink">
              Voltar aos contatos
            </Link>
          </div>
        ) : (
          <>
            {/* ── Hero ── */}
            <div className="cd-hero" style={heroAccent}>
              <span className="cd-pop">
                <PersonAvatar
                  className="cd-avatar"
                  name={contact.displayName}
                  color={catColor(contact.category, customCategories)}
                  photoUrl={photoUrl}
                />
              </span>
              <div className="cd-heroinfo">
                <span className="cd-cat">{info.label}</span>
                <h1 className="cd-name">{contact.displayName || 'Sem nome'}</h1>
                {contact.phone && <p className="cd-phone">{contact.phone}</p>}
                <div className="cd-meta">
                  {contact.interactionCount > 0 && (
                    <span>
                      {contact.interactionCount}{' '}
                      {contact.interactionCount === 1 ? 'conversa' : 'conversas'}
                    </span>
                  )}
                  {formatWhen(contact.lastInteractionAt) && (
                    <span>· {formatWhen(contact.lastInteractionAt)}</span>
                  )}
                </div>
                {contact.appUsername && <p className="cd-onapp">@{contact.appUsername}</p>}
                {business && (
                  <p className="cd-pro">
                    <ProAccountTag />
                  </p>
                )}
              </div>
            </div>

            {patientLink && (
              // Fase E: o contato também é paciente da clínica de quem abre (lido da clínica).
              <Link
                href={`/negocio/clinica/pacientes/${patientLink.patientId}`}
                className="cd-patient"
              >
                <span>
                  <b>Também é paciente da {patientLink.clinicName}</b>
                  <small>{PATIENT_STATUS_LABEL[patientLink.status]} · Abrir a ficha</small>
                </span>
                <span aria-hidden="true">›</span>
              </Link>
            )}

            {/* ── Como o secretário trata ── */}
            <section className="cd-sec">
              <div className="cd-lbl">
                Como <b>{agentName}</b> trata {nome}
              </div>
              <div className={`cd-modes${busyMode ? ' busy' : ''}`}>
                {MODES.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`cd-mode${mode === m.id ? ' on' : ''}`}
                    onClick={() => void applyMode(m.id)}
                    disabled={busyMode}
                  >
                    <span className="cd-modee">{m.icon}</span>
                    <span className="cd-modet">
                      <b>{m.title}</b>
                      <small>{m.desc(nome)}</small>
                    </span>
                    <span className="cd-radio" aria-hidden="true" />
                  </button>
                ))}
              </div>
            </section>

            {/* ── Categoria (etiquetas: cor + nome) ── */}
            <section className="cd-sec">
              <div className="cd-lbl">Quem é {nome} pra você</div>
              <div className="cd-cats">
                {CATEGORY_CHOICES.map((cc) => (
                  <button
                    key={cc.value}
                    type="button"
                    className={`cd-catcard${category === cc.value ? ' on' : ''}`}
                    style={{ ['--cc' as string]: catInfo(cc.value).c } as React.CSSProperties}
                    aria-pressed={category === cc.value}
                    onClick={() => void pickCategory(cc.value)}
                  >
                    <span className="cd-catav" aria-hidden="true" />
                    <span className="cd-catnm">{cc.label}</span>
                  </button>
                ))}
                {customCategories.map((cc) => (
                  <button
                    key={cc.id}
                    type="button"
                    className={`cd-catcard${category === cc.id ? ' on' : ''}`}
                    style={{ ['--cc' as string]: cc.c } as React.CSSProperties}
                    aria-pressed={category === cc.id}
                    onClick={() => void pickCategory(cc.id)}
                  >
                    <span className="cd-catav" aria-hidden="true" />
                    <span className="cd-catnm">{cc.label}</span>
                  </button>
                ))}
                <button
                  type="button"
                  className="cd-catcard cd-catcard-add"
                  onClick={() => setShowNewCat(true)}
                >
                  <span className="cd-catav cd-catav-add" aria-hidden="true">
                    +
                  </span>
                  <span className="cd-catnm">Criar</span>
                </button>
              </div>
            </section>

            {showNewCat && (
              <section className="cd-card cd-newcat">
                <div className="cd-fld">
                  <label>Nome da categoria</label>
                  <input
                    value={newCatName}
                    maxLength={30}
                    onChange={(e) => setNewCatName(e.target.value)}
                    placeholder="Ex.: Aniversariantes, Vizinhos…"
                    autoFocus
                  />
                </div>
                <div className="cd-newcat-act">
                  <button
                    type="button"
                    className="cd-newcat-cancel"
                    onClick={() => {
                      setShowNewCat(false);
                      setNewCatName('');
                    }}
                    disabled={savingNewCat}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="cd-save"
                    onClick={() => void createCustomCategory()}
                    disabled={savingNewCat || !newCatName.trim()}
                  >
                    {savingNewCat ? 'Criando…' : 'Criar categoria'}
                  </button>
                </div>
              </section>
            )}

            {/* ── Detalhes que ajudam ── */}
            <section className="cd-card">
              <div className="cd-fld">
                <label>Nome do contato</label>
                <input
                  value={name}
                  maxLength={60}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Como essa pessoa se chama"
                />
              </div>
              <div className="cd-fld">
                <label>
                  Como {agentName} deve chamar? <i>opcional</i>
                </label>
                <input
                  value={preferredName}
                  maxLength={40}
                  onChange={(e) => setPreferredName(e.target.value)}
                  placeholder={`Ex.: ${nome}, Tia, Dr. Silva…`}
                />
              </div>
              <div className="cd-fld">
                <label>O que o secretário deve saber</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ex.: é meu contador, prefere áudio, sempre responder com carinho…"
                  rows={3}
                />
              </div>
              <button className="cd-save" onClick={() => void saveInfo()} disabled={savingInfo}>
                {savedInfo ? 'Salvo! ✓' : savingInfo ? 'Salvando…' : 'Salvar detalhes'}
              </button>
            </section>

            {/* ── O que rolou ── */}
            <section className="cd-card">
              <h3 className="cd-h3">O que rolou com {nome}</h3>
              {timeline.length === 0 ? (
                <p className="cd-tl-empty">
                  Assim que {nome} falar com você, o histórico aparece aqui
                </p>
              ) : (
                <ul className="cd-tl">
                  {timeline.map((t) => (
                    <li key={t.id} className={`cd-tli ${t.kind}`}>
                      <span className="cd-dot" />
                      <div className="cd-tlt">
                        <p>{t.text}</p>
                        <small>
                          {t.kind === 'wait' ? 'Esperou seu sim' : 'Secretário cuidou'} · {t.when}
                        </small>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <button className="cd-danger" onClick={() => navigate('/contatos')}>
              Voltar aos contatos
            </button>
          </>
        )}
      </div>
      {inviting &&
        contact &&
        createPortal(
          <div
            className="rk-backdrop"
            role="dialog"
            aria-label="Convidar para o Secretário"
            onClick={(event) => {
              if (event.target === event.currentTarget) setInviting(false);
            }}
          >
            <div className="rk-sheet">
              <span className="rk-handle" aria-hidden="true" />
              {/* Quem fala aqui é o Secretário: o personagem é o dele. */}
              <div className="rk-stage">
                <img
                  src={characterImage(
                    profileQ.data?.characterKey ?? DEFAULT_CHARACTER,
                    'simpatico',
                  )}
                  alt=""
                />
              </div>
              <div className="rk-kicker">Convite</div>
              <h2>{contact.displayName || 'Essa pessoa'} ainda não está aqui</h2>
              <p>
                Mando um convite com o seu @usuário. Quando a pessoa entrar, a conversa de vocês já
                nasce protegida.
              </p>
              {inviteNote && <p className="rk-note">{inviteNote}</p>}
              <button className="rk-btn" onClick={() => void sendInvite()}>
                Enviar convite
              </button>
              <button className="rk-ghost" type="button" onClick={() => setInviting(false)}>
                Agora não
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
